import React from "react";
import {Alert, Button, Form, Modal, Tabs, Tab} from "react-bootstrap";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {ADMIN_LANG} from "../../../settings";
import AdminLoader from ".././AdminLoader";
import {closeModal, saveSectionPdf} from "../../../app/slices/modals/sectionPdfModal";
import PdfProcessingProgress from "./PdfProcessingProgress";
import { v4 as uuidv4 } from 'uuid';

interface LanguagePdfs {
    [key: string]: File | null;
}

const SectionPdfModal = () => {
    const dispatch = useAppDispatch();
    const {isOpen, loading, error, section_id, isProcessing} = useAppSelector((state) => state.sectionPdfModal);
    const { game_id, current_version_id, active_languages } = useAppSelector((state) => state.adminGameView);
    const [languagePdfs, setLanguagePdfs] = React.useState<LanguagePdfs>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleClose = () => {
        dispatch(closeModal());
    }

    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        if(isProcessing || isSubmitting){
            return;
        }

        e.preventDefault();
        const modalForm = e.currentTarget;

        if (!modalForm.checkValidity()) {
            e.stopPropagation();
        } else {
            setIsSubmitting(true);
            const formData = new FormData(modalForm);
            formData.append('section_id', section_id);
            formData.append('game_id', game_id);
            formData.append('version_id', current_version_id);

            Object.entries(languagePdfs).forEach(([lang, file]) => {
                if (file) {
                    formData.append(`pdf_${lang}`, file, uuidv4() + file.name);
                }
            });

            dispatch(saveSectionPdf(formData))
                .finally(() => setIsSubmitting(false));
        }
    }

    const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>, language:string) => {
        if (e.target.files && e.target.files.length > 0) {
            setLanguagePdfs(prev => ({
                ...prev,
                [language]: e.target.files![0]
            }));
        } else {
            setLanguagePdfs(prev => ({
                ...prev,
                [language]: null
            }));
        }
    }

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{ADMIN_LANG.upload_pdf}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                <Form id='section-pdf-form' onSubmit={handleSave}>
                        {active_languages.map(language => (
                            <Form.Group key={language} controlId={`pdf-${language}`} className="mt-3">
                                <Form.Label>{ADMIN_LANG.pdf_file} ({language.toUpperCase()})</Form.Label>
                                <Form.Control
                                    type='file'
                                    accept='application/pdf'
                                    onChange={(e:React.ChangeEvent<HTMLInputElement>) => handlePdfChange(e, language)}
                                />
                                <Form.Text className='help-text' dangerouslySetInnerHTML={{__html:ADMIN_LANG.pdf_file_help}}></Form.Text>
                            </Form.Group>
                        ))}
                </Form>
                <PdfProcessingProgress />
            </Modal.Body>
            <Modal.Footer>
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' form='section-pdf-form' type='submit' disabled={isProcessing || isSubmitting}>
                    {ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SectionPdfModal;